import { CreditCard, Shield, BarChart3, Bell, Zap, Search, Eye, RefreshCw } from 'lucide-react'
import { APP_NAME, APP_TAGLINE } from '../../constants'

const features = [
  { icon: CreditCard, title: 'Unified Card Tracking', desc: 'See all your cards and bank accounts in one dashboard' },
  { icon: Search, title: 'Subscription Detection', desc: 'Automatically finds recurring charges so nothing hides from you' },
  { icon: Bell, title: 'Large Transaction Alerts', desc: 'Set your own threshold — get flagged when big charges hit' },
  { icon: BarChart3, title: 'Rewards Optimization', desc: 'Know which card to use for maximum cashback and points' },
  { icon: Shield, title: 'Late Fee Prevention', desc: 'See upcoming due dates before they become penalties' },
  { icon: Eye, title: 'Unusual Activity Flags', desc: 'Spot charges that don\'t match your normal spending patterns' },
  { icon: Zap, title: 'Real-Time Notifications', desc: 'Get alerted the moment a charge hits any of your cards' },
  { icon: RefreshCw, title: 'Smart Sync', desc: 'Automatic transaction pulls so your data is always current' },
]

export function WhySelffiPage() {
  return (
    <div className="space-y-12 max-w-3xl mx-auto pb-12">
      {/* Hero */}
      <div className="text-center">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-indigo-600 text-xl font-bold text-white">
          S
        </div>
        <h1 className="text-3xl font-bold text-white sm:text-4xl">
          Why I Built <span className="text-indigo-400">{APP_NAME}</span>
        </h1>
        <p className="mt-3 text-sm text-slate-500">{APP_TAGLINE}</p>
      </div>

      {/* Founder Narrative */}
      <div className="rounded-2xl border border-slate-700/50 bg-slate-800/60 p-6 sm:p-10">
        <div className="prose-sm sm:prose leading-relaxed text-slate-300 space-y-5">
          <p className="text-lg text-slate-200 font-medium">My name is Karthik, and I built {APP_NAME} because I was tired.</p>
          <p>
            Tired of watching money slip through the cracks — a forgotten subscription here, a late fee there, a hidden charge I didn't catch until it was too late. I was using the wrong credit cards for the wrong purchases, missing out on cashback and rewards I'd already earned the right to. I had multiple bank accounts, multiple cards, and zero clarity.
          </p>
          <p>
            I didn't need another budgeting app that guilt-tripped me with pie charts. I needed something that actually watched my back — something that would flag the charges that didn't belong, remind me which card to use at the grocery store, and tell me when a subscription I forgot about just renewed for another year.
          </p>
          <p>
            So I built {APP_NAME}. Not to sell you a financial philosophy. Just to give you the visibility and control that your bank should have offered you all along.
          </p>
        </div>
      </div>

      {/* Feature Grid */}
      <div>
        <h2 className="mb-6 text-xl font-bold text-white text-center">What {APP_NAME} Does</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {features.map((f, i) => (
            <div key={i} className="flex items-start gap-4 rounded-xl border border-slate-700/50 bg-slate-800/80 p-5 transition-all hover:border-indigo-500/40 hover:bg-slate-800">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-indigo-500/20 text-indigo-400">
                <f.icon className="h-5 w-5" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-white">{f.title}</h3>
                <p className="mt-1 text-sm leading-relaxed text-slate-400">{f.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Closing */}
      <div className="rounded-2xl border border-indigo-500/30 bg-indigo-500/10 p-8 text-center">
        <h2 className="text-xl font-bold text-white">{APP_TAGLINE}</h2>
        <p className="mt-3 text-sm text-slate-400 max-w-lg mx-auto leading-relaxed">
          {APP_NAME} doesn't sell your data. We don't push products. We just watch your back — so you can spend with confidence and save without stress.
        </p>
      </div>
    </div>
  )
}
